import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Query,
  ScheduleInstance,
  ScheduleInstanceFilterInput,
  ScheduleInstanceOrderByFields,
  OrderByDirection,
} from "@models/ISchema";
import {
  GetProgrammingSlotsProps,
  MicroprogrammingPreviewProps,
  ProgrammingSessionCrudInput,
} from "@services/types/programming-edit/programmingEditTypes";
import { SCHEDULE_INSTANCES_QUERY } from "./graphql/programmingSlots.query";
import { MICROPROGRAMMING_PREVIEW_QUERY } from "./graphql/microprogrammingPreview.query";
import { PROGRAMMING_SESSIONS_CRUD } from "./graphql/programmingSessionsCrud.mutation";

/**
 * Hook to fetch schedule instances for a given link.
 * Each instance represents a scheduled date with its sections and sessions.
 * @returns Handler to trigger the query + loading state + raw ScheduleInstance[]
 */
export const useGetProgrammingSlots = () => {
  const [getScheduleInstances, { loading, data }] = useLazyQuery<Query>(SCHEDULE_INSTANCES_QUERY);

  const handleGet = ({ scenarioId, originId, linkId }: GetProgrammingSlotsProps) => {
    const filter: ScheduleInstanceFilterInput = {
      fields: { linkId: { eq: linkId } },
      orderBy: [{ field: ScheduleInstanceOrderByFields.Date, direction: OrderByDirection.Asc }],
    };

    getScheduleInstances({ variables: { scenarioId, originId, filter } });
  };

  const instances: ScheduleInstance[] | null = (data as any)?.cube?.scheduleInstances ?? null;

  return {
    getProgrammingSlots: handleGet,
    loading,
    slots: instances,
  };
};

/**
 * Hook to run the microprogramming preview query.
 * @returns Handler to trigger the query + loading state + raw preview result
 */
export const useMicroprogrammingPreview = () => {
  const [executePreview, { loading, data }] = useLazyQuery<Query>(MICROPROGRAMMING_PREVIEW_QUERY);

  const handlePreview = ({ scenarioId, originId, input }: MicroprogrammingPreviewProps) => {
    return executePreview({ variables: { scenarioId, originId, input } });
  };

  const previewResult = (data as any)?.cube?.microprogrammingPreview ?? null;

  return {
    getMicroprogrammingPreview: handlePreview,
    loading,
    previewResult,
  };
};

interface ProgrammingCrudProps {
  scenarioId: string;
  originId: string;
  input: ProgrammingSessionCrudInput;
}

/**
 * Hook to submit the programming changeset via sessionsCrud mutation.
 * Used with dryRun=true in ProgrammingEdit (validation) and dryRun=false in ResultEdit (commit).
 */
export const useProgrammingSessionsCrud = () => {
  const [sessionsCrudMutation, { loading }] = useMutation(PROGRAMMING_SESSIONS_CRUD);

  const submitCrud = async ({ scenarioId, originId, input }: ProgrammingCrudProps) => {
    const { data } = await sessionsCrudMutation({ variables: { scenarioId, originId, input } });
    return (data as any)?.cube?.sessionsCrud ?? null;
  };

  return { submitCrud, loading };
};
